/**
 * Детальная страница заказа швейного цеха.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import {
  ActionPanel,
  Button,
  DataTable,
  DocumentBlock,
  ExternalPageCanvas,
  PageIsland,
} from '../uikit';
import type { DocumentItem } from '../uikit';
import type { IDataTableColumn } from '../uikit';
import { IconEdit } from '../uikit/components/icons';
import type { OrderDTO, OrderItemDTO } from '../api/orders';
import { findOrdersByIDs, ORDER_STATUS_LABELS } from '../api/orders';
import { toUserMessage } from '../api/client';
import { findProductsByIDs } from '../api/products';
import type { Product } from '../api/products';
import type { ShiftListItemDTO } from '../api/shifts';
import { findShifts, SHIFT_STATUS_LABELS } from '../api/shifts';
import { findEmployeesByIDs } from '../api/employees';
import { formatDate, formatDateTime } from '../utils/formatDate';
import { formatFullName } from '../utils/formatFullName';
import { formatInteger, formatMoney } from '../utils/formatNumber';

const ErrorActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.space[1]}px;
  flex-wrap: wrap;
`;

const MetaBlock = styled.div`
  margin: 0 0 ${({ theme }) => theme.space[2]}px;
  font-size: ${({ theme }) => theme.fontSizes[5]}px;
  color: ${({ theme }) => theme.colors.text.secondary};

  p {
    margin: ${({ theme }) => theme.space[0]}px 0 0;
  }

  p:first-child {
    margin: 0;
  }
`;

const PageTitle = styled.h2`
  margin: 0 0 ${({ theme }) => theme.space[2]}px;
  font-size: ${({ theme }) => theme.fontSizes[9]}px;
  line-height: 1.5;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const LoadingText = styled.p`
  margin: ${({ theme }) => theme.space[4]}px 0 0;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.fontSizes[5]}px;
`;

const EditButtonContent = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
`;

interface OrderWithProductNames extends OrderItemDTO {
  productName?: string;
}

function getOrderItemColumns(): IDataTableColumn<OrderWithProductNames>[] {
  return [
    {
      key: 'productName',
      header: 'Изделие',
      sortable: false,
      fixedWidth: 200,
      cellStyle: { px: 2 },
      render: (value) => (value ? String(value) : '—'),
    },
    {
      key: 'quantity',
      header: 'Кол-во, шт',
      sortable: false,
      fixedWidth: 100,
      cellStyle: { px: 2 },
      render: (value) => formatInteger(value as number | null),
    },
    {
      key: 'costPrice',
      header: 'Себестоимость, руб',
      sortable: false,
      fixedWidth: 140,
      cellStyle: { px: 2 },
      render: (value) => formatMoney(value as string | null),
    },
    {
      key: 'salePrice',
      header: 'Цена продажи, руб',
      sortable: false,
      fixedWidth: 140,
      cellStyle: { px: 2 },
      render: (value) => formatMoney(value as string | null),
    },
    {
      key: 'diff',
      header: 'Разница, руб',
      sortable: false,
      fixedWidth: 120,
      cellStyle: { px: 2 },
      render: (value) => formatMoney(value as string | null),
    },
  ];
}

function getOrderShiftColumns(
  related: {
    employeesMap: Record<string, { familyName?: string; givenName?: string; patronymic?: string }>;
  },
): IDataTableColumn<ShiftListItemDTO>[] {
  return [
    {
      key: 'shiftDate',
      header: 'Дата смены',
      sortable: false,
      fixedWidth: 120,
      cellStyle: { px: 2 },
      render: (value, item) => (
        <Link
          to={`/shifts/${item.id}`}
          style={{ color: 'inherit', textDecoration: 'none', fontWeight: 500 }}
        >
          {formatDate(value as string)}
        </Link>
      ),
    },
    {
      key: 'employeeId',
      header: 'Сотрудник',
      sortable: false,
      fixedWidth: 200,
      cellStyle: { px: 2 },
      render: (value) => {
        const id = value as string;
        const emp = id ? related.employeesMap[id] : undefined;
        if (emp) {
          return formatFullName(emp.familyName, emp.givenName, emp.patronymic) || '—';
        }
        return id ? `${id.slice(0, 8)}…` : '—';
      },
    },
    {
      key: 'status',
      header: 'Статус',
      sortable: false,
      fixedWidth: 130,
      cellStyle: { px: 2 },
      render: (value) => {
        const status = value as string;
        return status
          ? (SHIFT_STATUS_LABELS[status as keyof typeof SHIFT_STATUS_LABELS] ?? status)
          : '—';
      },
    },
    {
      key: 'totalPlanQty',
      header: { text: 'План, шт', headerCellStyle: { textAlign: 'right' } },
      sortable: false,
      fixedWidth: 88,
      cellStyle: { px: 2, textAlign: 'right' },
      render: (_, item) => formatInteger(item.totalPlanQty ?? 0),
    },
    {
      key: 'totalFactQty',
      header: { text: 'Факт, шт', headerCellStyle: { textAlign: 'right' } },
      sortable: false,
      fixedWidth: 88,
      cellStyle: { px: 2, textAlign: 'right' },
      render: (_, item) => formatInteger(item.totalFactQty ?? 0),
    },
    {
      key: 'totalPayment',
      header: { text: 'Оплата, руб', headerCellStyle: { textAlign: 'right' } },
      sortable: false,
      fixedWidth: 120,
      cellStyle: { px: 2, textAlign: 'right' },
      render: (_, item) => formatMoney(item.totalPayment),
    },
    {
      key: 'closedAt',
      header: 'Закрыта',
      sortable: false,
      fixedWidth: 140,
      cellStyle: { px: 2 },
      render: (value) => formatDateTime(value as string | null),
    },
  ];
}

export function OrderPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const [order, setOrder] = useState<OrderDTO | null>(null);
  const [productNames, setProductNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [shifts, setShifts] = useState<ShiftListItemDTO[]>([]);
  const [shiftsEmployeesMap, setShiftsEmployeesMap] = useState<
    Record<string, { familyName?: string; givenName?: string; patronymic?: string }>
  >({});
  const [shiftsLoading, setShiftsLoading] = useState(false);
  const [shiftsError, setShiftsError] = useState<string | null>(null);

  const loadOrder = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setLoadError(null);
    try {
      const res = await findOrdersByIDs([id]);
      const found = res.items[id] ?? null;
      setOrder(found ?? null);

      if (found?.items?.length) {
        const productIds = [...new Set(found.items.map((it) => it.productId))];
        const productsRes = await findProductsByIDs(productIds);
        const names: Record<string, string> = {};
        for (const [pid, p] of Object.entries(productsRes.items)) {
          names[pid] = (p as Product).name;
        }
        setProductNames(names);
      } else {
        setProductNames({});
      }
    } catch (err) {
      setOrder(null);
      setProductNames({});
      setLoadError(toUserMessage(err));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void loadOrder();
  }, [loadOrder]);

  const loadShiftsForOrder = useCallback(async () => {
    if (!id) return;
    setShiftsLoading(true);
    setShiftsError(null);
    try {
      const res = await findShifts({
        orderId: id,
        page: 1,
        itemsPerPage: 100,
        order: [{ field: 'shiftDate', direction: 'desc' }],
      });
      const list = res.items ?? [];
      setShifts(list);
      const employeeIds = [...new Set(list.map((s) => s.employeeId).filter(Boolean))];
      if (employeeIds.length > 0) {
        const emRes = await findEmployeesByIDs(employeeIds);
        setShiftsEmployeesMap(emRes.items ?? {});
      } else {
        setShiftsEmployeesMap({});
      }
    } catch (err) {
      setShifts([]);
      setShiftsEmployeesMap({});
      setShiftsError(toUserMessage(err));
    } finally {
      setShiftsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (!id || !order) {
      setShifts([]);
      setShiftsEmployeesMap({});
      setShiftsError(null);
      return;
    }
    void loadShiftsForOrder();
  }, [id, order, loadShiftsForOrder]);

  if (loading) {
    return (
      <ExternalPageCanvas>
        <LoadingText>Загрузка…</LoadingText>
      </ExternalPageCanvas>
    );
  }

  if (!order) {
    return (
      <ExternalPageCanvas>
        <ActionPanel defaultPath="/orders" />
        <PageIsland>
          <p style={{ margin: 0, color: theme.colors.text.secondary }}>
            {loadError ? loadError : 'Заказ не найден'}
          </p>
          {loadError && (
            <ErrorActions style={{ marginTop: theme.space[2] }}>
              <Button variant="filled" color="primary" onClick={() => void loadOrder()}>
                Повторить
              </Button>
            </ErrorActions>
          )}
        </PageIsland>
      </ExternalPageCanvas>
    );
  }

  const itemsWithNames: OrderWithProductNames[] = (order.items ?? []).map((it) => ({
    ...it,
    productName: productNames[it.productId] ?? it.productId,
  }));

  const statusLabel = ORDER_STATUS_LABELS[order.status as keyof typeof ORDER_STATUS_LABELS] ?? order.status;

  return (
    <ExternalPageCanvas>
      <ActionPanel
        defaultPath="/orders"
        rightAddon={
          <Button
            variant="text"
            color="secondary"
            size="s"
            onClick={() => navigate(`/orders/${id}/edit`)}
          >
            <EditButtonContent>
              <IconEdit />
              Редактировать
            </EditButtonContent>
          </Button>
        }
      />
      <PageIsland>
        {loadError && (
          <p
            role="alert"
            style={{ margin: `0 0 ${theme.space[2]}px`, color: theme.colors.error.main }}
          >
            {loadError}
          </p>
        )}
        <PageTitle>{order.title}</PageTitle>
        <MetaBlock>
          <p>Заказчик: {order.customer || '—'}</p>
          <p>Дата: {formatDate(order.date)}</p>
          <p>Статус: {statusLabel}</p>
          <p>Кол-во, шт (по заказу): {formatInteger(order.quantity)}</p>
        </MetaBlock>
      </PageIsland>

      {(order.documents?.length ?? 0) > 0 && (
        <PageIsland title="Документы">
          <DocumentBlock
            id="order-detail-documents"
            value={
              (order.documents ?? []).map(
                (d): DocumentItem => ({
                  file: {
                    id: d.file.id,
                    uri: d.file.uri,
                    filename: d.file.filename,
                    size: d.file.size,
                  },
                }),
              )
            }
            onChange={() => {}}
            readOnly
          />
        </PageIsland>
      )}

      {itemsWithNames.length > 0 && (
        <PageIsland title="Позиции">
          <DataTable
            data={itemsWithNames}
            columns={getOrderItemColumns()}
            settings={{ horizontalLine: true }}
            minHeight={100}
            placeholder={{ text: 'Нет позиций' }}
          />
        </PageIsland>
      )}

      <PageIsland title="Смены по заказу">
        {shiftsLoading && (
          <p style={{ margin: 0, color: theme.colors.text.secondary }}>Загрузка смен…</p>
        )}
        {shiftsError && (
          <div style={{ marginBottom: theme.space[2] }}>
            <p role="alert" style={{ margin: `0 0 ${theme.space[1]}px`, color: theme.colors.error.main }}>
              {shiftsError}
            </p>
            <Button variant="outlined" color="primary" size="s" onClick={() => void loadShiftsForOrder()}>
              Повторить
            </Button>
          </div>
        )}
        {!shiftsLoading && !shiftsError && (
          <DataTable
            data={shifts}
            columns={getOrderShiftColumns({ employeesMap: shiftsEmployeesMap })}
            settings={{ horizontalLine: true }}
            minHeight={80}
            placeholder={{ text: 'Нет смен по этому заказу' }}
          />
        )}
      </PageIsland>
    </ExternalPageCanvas>
  );
}
